import { investorApi } from '@core/api/httpClient'
import { OFFERINGS_ENDPOINTS } from './browseEndpointPaths'
import type { PropertyStatus } from '../constants/propertyStatus'
import type { ActivePropertyFilters } from '../types/propertyTypes'
import type {
  OfferingsListWireResponseDto,
  PropertyFilterOptionsWireDto,
} from './contracts/propertyContracts'

interface ListParams {
  propertyStatus: PropertyStatus
  page: number
  limit: number
  search?: string
  filters?: ActivePropertyFilters
}

// Per-key serializers — each owns one fragment of the URL, so the dispatcher
// reads as a flat list. All keys go on the wire in camelCase to match the
// backend GetOfferingsRequestDto.
const appendMulti = (params: URLSearchParams, key: string, values?: string[]): void => {
  values?.forEach((value) => params.append(key, value))
}
const setIfPresent = (params: URLSearchParams, key: string, value?: string): void => {
  if (value) params.set(key, value)
}

const serializeListParams = ({
  propertyStatus,
  page,
  limit,
  search,
  filters,
}: ListParams): string => {
  const params = new URLSearchParams()
  params.set('propertyStatus', propertyStatus)
  params.set('page', String(page))
  params.set('limit', String(limit))
  setIfPresent(params, 'search', search?.trim() || undefined)
  appendMulti(params, 'locations', filters?.locations)
  appendMulti(params, 'propertyTypes', filters?.propertyTypes)
  appendMulti(params, 'rois', filters?.rois)
  setIfPresent(params, 'sort', filters?.sort)
  setIfPresent(params, 'tokenPriceMin', filters?.tokenPriceMin)
  setIfPresent(params, 'tokenPriceMax', filters?.tokenPriceMax)
  setIfPresent(params, 'appreciationRangeMin', filters?.appreciationRangeMin)
  setIfPresent(params, 'appreciationRangeMax', filters?.appreciationRangeMax)
  if (filters?.bookmarkedOnly) params.set('isBookmarked', 'true')
  return params.toString()
}

export interface GetPropertiesArgs {
  status: PropertyStatus
  page: number
  limit: number
  search?: string
  filters?: ActivePropertyFilters
}

export async function getProperties(
  args: GetPropertiesArgs,
): Promise<OfferingsListWireResponseDto> {
  const { data } = await investorApi.get<OfferingsListWireResponseDto>(
    `${OFFERINGS_ENDPOINTS.OFFERINGS}?${serializeListParams({
      propertyStatus: args.status,
      page: args.page,
      limit: args.limit,
      search: args.search,
      filters: args.filters,
    })}`,
  )
  return data
}

export async function getPropertyFilters(
  status: PropertyStatus,
): Promise<PropertyFilterOptionsWireDto> {
  const { data } = await investorApi.get<PropertyFilterOptionsWireDto>(
    OFFERINGS_ENDPOINTS.OFFERINGS_FILTERS,
    { params: { propertyStatus: status } },
  )
  return data
}

export async function addBookmark(offeringId: string): Promise<void> {
  await investorApi.post(OFFERINGS_ENDPOINTS.BOOKMARK.replace('{offeringId}', offeringId))
}

export async function removeBookmark(offeringId: string): Promise<void> {
  await investorApi.delete(OFFERINGS_ENDPOINTS.BOOKMARK.replace('{offeringId}', offeringId))
}
